/* eslint-disable react/prop-types */
/* eslint-disable */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { setBusy, clearBusy } from 'irmlib/containers'
import { NatsService } from '../services/nats/natsService'
import { CUSOTM_LOCAL_AI_ERROR_CODES } from '../irmlib/src/common'
import { successToast, errorToast } from '../common/functions'
import CustomActionSnackBar from '../components/Pages/EdgeAI/CustomActionSnackBar'
import { getLocalServerUrl } from '../services/firestore/localGdeTransactions'
import { useTranslation } from 'react-i18next'
// ============================================
// 1. CONTEXT
// ============================================
const NatsContext = createContext(null)

// ============================================
// 2. CONSUMER HOOK
// ============================================
const NATS_UNAVAILABLE = Object.freeze({
  natsService: null,
  isConnecting: false,
  isConnected: false,
  connectionError: false,
  retryConnection: () => {
    console.warn('Nats data is null or out scope of provider.')
  },
})

const useNats = () => {
  const context = useContext(NatsContext)
  if (!context) {
    console.warn('useNats() called outside of <NatsProvider>. Returning safe defaults.')
    return NATS_UNAVAILABLE
  }
  return context
}

// ============================================
// 3. PROVIDER COMPONENT
// ============================================
const MAX_RETRY_COUNT = 3

const NatsProvider = ({ children }) => {
  // --- NatsService instance (useRef, not useMemo — safe in React 18 Strict Mode) ---
  const natsServiceRef = useRef(null)
  if (!natsServiceRef.current) {
    natsServiceRef.current = new NatsService()
  }
  const natsService = natsServiceRef.current
  const { t } = useTranslation()

  // --- Connection state ---
  const [connectionState, setConnectionState] = useState({
    isConnecting: true,
    isConnected: false,
    connectionError: false,
    maxFailedAttempts: false,
    isReconnecting: false,
    connectionRestoreSuccess: false,
    connectionErrorWhileRetry: false
  })

  // --- Snackbar state ---
  const [snackBarController, setSnackBarController] = useState({
    showSnackBar: false,
    message: '',
  })

  // --- Reconnect toast display snapshot (prevents content flashing during exit animation) ---
  // const [reconnectToast, setReconnectToast] = useState({
  //   severity: 'warning',
  //   message: '',
  // })

  // --- Retry counter ---
  const retryCount = useRef(0)

  // --- State updater passed into NatsService.getConnection ---
  const updateConnectionState = useCallback((state) => {
    setConnectionState(prevState => ({ ...prevState, ...state }))
  }, [])

  // --- Core connect logic ---
  const connectToNats = useCallback(async () => {
    try {
      setBusy()
      // for directly modifying the serverUrl from here in future if there is only one url or for better code stucture we need to move this to natsService
      if (!natsService.serverURL) {
        const localAiServerURL = await getLocalServerUrl()
        natsService.serverURL = localAiServerURL.origin
      }
      const natsConn = await natsService.getConnection(natsService.serverURL, {
        updateConnectionState,
        isManualorInitiaConnection: true,
      })
      if (natsConn) {
        successToast({ message: t('smartCatalogLocal.messages.localAIConnectionSuccess') , autoHideDuration: 1000, isBlocking: false })
      }
    } catch (error) {
      console.error('Error establishing NATS connection:', error)
      let errorMsg = 'localAIGenConnErr'
      if (error?.status === CUSOTM_LOCAL_AI_ERROR_CODES.CONNECTION_STRING_NOT_FOUND) {
        errorMsg = 'edgeDeviceNotFound'
        updateConnectionState({ isConnecting: false })
      }
      setSnackBarController(prev => ({
        ...prev,
        showSnackBar: true,
        message: t(`smartCatalogLocal.messages.${errorMsg}`),
      }))
    } finally {
      clearBusy()
    }
  }, [natsService, updateConnectionState])

  // --- Retry logic ---
  const retryConnection = useCallback(async () => {
    setSnackBarController(prev => ({ ...prev, showSnackBar: false, message: '' }))
    retryCount.current++
    try {
      setBusy()
      if (!natsService.serverURL) {
        const localAiServerURL = await getLocalServerUrl()
        natsService.serverURL = localAiServerURL.origin
      }
      const natsConn = await natsService.getConnection(natsService.serverURL, {
        updateConnectionState,
        isManualorInitiaConnection: true,
      })
      if (natsConn) {
        retryCount.current = 0 // reset on success
        successToast({ message:  t('smartCatalogLocal.messages.localAIConnectionSuccess'), autoHideDuration: 1000, isBlocking: false })
      }
    } catch (error) {
      console.error('Error establishing NATS connection:', error)

      if (error?.status === CUSOTM_LOCAL_AI_ERROR_CODES.CONNECTION_STRING_NOT_FOUND) {
        errorToast({ message: t('smartCatalogLocal.messages.edgeDeviceNotFound'), isBlocking: false, autoHideDuration: 2000 })
        updateConnectionState({ isConnecting: false })
        return
      }
      if (retryCount.current < MAX_RETRY_COUNT) {
        setSnackBarController(prev => ({
          ...prev,
          showSnackBar: true,
          message:  t('smartCatalogLocal.messages.localAIMaxRetryCountErr', { countLeft: MAX_RETRY_COUNT - retryCount.current }),
          // message: `Error establishing NATS connection — reattempts left: ${MAX_RETRY_COUNT - retryCount.current}`,
        }))
      } else {
        errorToast({ message:  t('smartCatalogLocal.messages.localAIConnetionErr'), isBlocking: false, autoHideDuration: 2000 })
      }
    } finally {
      clearBusy()
    }
  }, [natsService, updateConnectionState])

  // --- Snackbar close ---
  const closeConnectionSnackBar = useCallback(() => {
    setSnackBarController(prev => ({ ...prev, showSnackBar: false, message: '' }))
  }, [])

  // --- Effect: connect on mount, dispose on unmount ---
  useEffect(() => {
    natsService.registerStateCallback(updateConnectionState)
    connectToNats()
    // clean up on component unmount
    return () => {
      natsService.dispose()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // --- Snapshot reconnect toast content (only updates when opening/changing, NOT when closing) ---
  // useEffect(() => {
  //   if (connectionState.connectionRestoreSuccess) {
  //     setReconnectToast({
  //       severity: 'success',
  //       message: 'Connection successfully restored.......',
  //     })
  //   } else if (connectionState.isReconnecting) {
  //     setReconnectToast({
  //       severity: 'warning',
  //       message: 'Connection lost wait while we are reconnecting to local AI server......',
  //     })
  //   }else if(connectionState.connectionErrorWhileRetry){
  //     errorToast({
  //       severity: 'error',
  //       message: 'Sorry Connection failed...',
  //     })
  //   }
  //   // When both are false (closing), intentionally do NOT update — keeps last content during exit animation
  // }, [connectionState.isReconnecting, connectionState.connectionRestoreSuccess])

  // --- Snackbar action config ---
  const snackBarActions = useMemo(() => ({
    primary: {
      label: 'Retry',
      handler: retryConnection,
    },
    secondary: {
      label: 'Close',
      handler: closeConnectionSnackBar,
    },
  }), [retryConnection, closeConnectionSnackBar])

  // --- Context value (memoized to prevent unnecessary re-renders) ---
  const contextValue = useMemo(() => ({
    natsService,
    isConnecting: connectionState.isConnecting,
    isConnected: connectionState.isConnected,
    connectionError: connectionState.connectionError,
    retryConnection,
  }), [connectionState, natsService, retryConnection])

  const getConnectMonitorSnackBarConfig = () => {
    if (connectionState.isReconnecting) {
      return {
        open: true,
        onClose: () => {},
        severity: 'warning',
        autoHideDuration: null,
        message: t('smartCatalogLocal.messages.locaAIConnectionLost'),
        actions: {},
        isBlocking: false
      }
    }
    if (connectionState.connectionRestoreSuccess) {
      return {
        open: true,
        onClose: () => {
          setConnectionState(prev => ({
            ...prev,
            connectionRestoreSuccess: false,
          }))
        },
        severity: 'success',
        autoHideDuration: 2000,
        message: t('smartCatalogLocal.messages.locaAIConnectionRestored'),
        actions: {},
        isBlocking: false
      }
    }
    if (connectionState.connectionErrorWhileRetry) {
      return {
        open: true,
        onClose: () => {
          setConnectionState(prev => ({
            ...prev,
            connectionErrorWhileRetry: false
          }))
        },
        severity: 'error',
        autoHideDuration: 2000,
        message: t('smartCatalogLocal.messages.localAIConnectionFailed'),
        actions: {},
        isBlocking: false
      }
    }
    return {
      open: false,
      onClose: () => {},
      severity: 'info',
      message: '',
      actions: {},
      isBlocking: false
    }
  }
  

  const monitorSnackBarConfig = getConnectMonitorSnackBarConfig()


  // --- Render ---
  return (
    <NatsContext.Provider value={contextValue}>
      {connectionState.isConnecting
        ? <div>Connecting to local AI server...</div>
        : children
      }
      {snackBarController.showSnackBar && (
        <CustomActionSnackBar
          open={snackBarController.showSnackBar}
          onClose={closeConnectionSnackBar}
          severity='error'
          message={snackBarController.message || ''}
          actions={snackBarActions}
          isBlocking={true}
        />
      )}

      {monitorSnackBarConfig.open && (
        <CustomActionSnackBar
          open={monitorSnackBarConfig.open}
          onClose={monitorSnackBarConfig.onClose}
          severity={monitorSnackBarConfig.severity}
          message={monitorSnackBarConfig.message}
          actions={monitorSnackBarConfig.actions}
          autoHideDuration={monitorSnackBarConfig.autoHideDuration}
          isBlocking={monitorSnackBarConfig.isBlocking}
        />
      )}

      {/* <CustomActionSnackBar
        open={connectionState.isReconnecting || connectionState.connectionRestoreSuccess}
        onClose={(event, reason) => {
          if(connectionState.connectionErrorWhileRetry){
            setConnectionState(prev => ({
              ...prev,
              connectionErrorWhileRetry: false,
            }))
            return
          }
          // While reconnecting, ignore all close attempts (keep the warning visible)
          if (connectionState.isReconnecting && !connectionState.connectionRestoreSuccess) {
            return
          }
          // Once connected, allow auto-hide and clickaway to dismiss
          setConnectionState(prev => ({
            ...prev,
            connectionRestoreSuccess: false,
          }))
        }}
        autoHideDuration={connectionState.connectionRestoreSuccess|| connectionState.connectionErrorWhileRetry ? 2000 : null}
        severity={reconnectToast.severity}
        message={reconnectToast.message}
        actions={{}}
        isBlocking={false}
      /> */}
    </NatsContext.Provider>
  )
}

NatsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

// ============================================
// 4. EXPORTS
// ============================================
export { NatsProvider, useNats }
